import { Card, Stack, ThemeIcon, Text, CardProps, Badge } from "@mantine/core"
import { useMatchRoute, useNavigate, useParams } from "@tanstack/react-router"
import "./index.css"
import { PostAttachments } from "./PostAttachments"
import { PostHeader } from "./PostHeader"
import {
  CommentVisibility,
  PostReactionsAndComments,
} from "./PostReactionsAndComments"
import { useGetAuthenticatedUser } from "../../../../-components/use-authenticated-user"
import { DefaultError } from "../../../../../../-components/error-context"
import { sanitizeHTML } from "../../../../../../utils/sanitize-html"
import { IconPencil, IconTrash } from "@tabler/icons-react"
import { DottedMenu } from "../../../../../../-components/dotted-menu"
import { openConfirmModal } from "@mantine/modals"
import { addStyleToLinks } from "../../../../-components/TextTile/addStyleToLinks"
import { useTranslation } from "react-i18next"
import { useDisclosure } from "@mantine/hooks"
import {
  getGetFeedPostsForUserQueryKey,
  getGetFeedPostsQueryKey,
  useDeletePost,
} from "@/orval/gen"
import { ChatPostDto } from "@/orval/gen/model"
import { useGetChatByIdSuspense } from "@/orval/gen"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { useQueryClient } from "@tanstack/react-query"
import { useBatchedSimpleUser } from "@/-dataloaders/simple-users"
import { useEffect, useRef, useState } from "react"
import { FeedPostFormModal } from "./FeedPostFormModal"

interface Props {
  feedPost: ChatPostDto
  hideComments?: CommentVisibility
  cardProps?: CardProps
  withControls?: boolean
}

export function FeedPostCard({
  feedPost,
  hideComments,
  cardProps,
  withControls = true,
}: Props) {
  const isCollapsed = hideComments === "collapsed"

  const { t } = useTranslation("app_server")
  const { data: chat } = useGetChatByIdSuspense({
    chatId: feedPost.chatId,
  })

  const { data: author } = useBatchedSimpleUser(feedPost.userId)

  const isOwnMessage = useGetAuthenticatedUser().data.userId === feedPost.userId

  const navigate = useNavigate()
  const postId = useParams({ strict: false }).postId
  const onCardClick = postId
    ? undefined
    : () =>
        navigate({
          to: "/app/com/feeds/$feedId/$postId",
          params: {
            feedId: feedPost.chatId.toString(),
            postId: feedPost.chatPostId.toString(),
          },
        })

  const matchesRoute = useMatchRoute()

  const queryClient = useQueryClient()

  const { mutateAsync: deletePost } = useDeletePost({
    mutation: {
      onError: showErrorNotification,
      onSuccess: () => {
        if (matchesRoute({ to: "/app/com/feeds/$feedId/$postId" })) {
          navigate({
            to: "/app/com/feeds/$feedId",
            params: { feedId: feedPost.chatId.toString() },
          })
        }
        queryClient.invalidateQueries({
          queryKey: getGetFeedPostsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getGetFeedPostsForUserQueryKey(),
        })
      },
    },
  })

  if (!chat) throw new DefaultError({ title: "Chat not found." })

  const [opened, { open, close }] = useDisclosure()
  const postTextRef = useRef<HTMLDivElement>(null)
  const [isPostTextTruncated, setIsPostTextTruncated] = useState(false)

  useEffect(() => {
    if (!isCollapsed) {
      setIsPostTextTruncated(false)
      return
    }

    const textElement = postTextRef.current
    if (!textElement) return

    const updateIsTruncated = () => {
      setIsPostTextTruncated(
        textElement.scrollHeight - textElement.clientHeight > 1
      )
    }

    updateIsTruncated()
    const resizeObserver = new ResizeObserver(updateIsTruncated)
    resizeObserver.observe(textElement)

    return () => {
      resizeObserver.disconnect()
    }
  }, [isCollapsed, feedPost.postText])

  const cardClassName = [
    onCardClick ? "topic-post-card" : undefined,
    isCollapsed ? "feed-post-card-collapsed" : undefined,
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <Card
      w="100%"
      shadow="sm"
      maw={800}
      withBorder
      onClick={onCardClick}
      style={{ flexShrink: 0 }}
      className={cardClassName || undefined}
      {...cardProps}
    >
      <Stack gap="xs" flex={1} h={"100%"}>
        <PostHeader
          author={author}
          authorSubtext={`#${chat.name}`}
          timeStamp={feedPost.timeStamp}
          rightSection={
            isOwnMessage && withControls ? (
              <DottedMenu
                items={[
                  {
                    leftSection: (
                      <ThemeIcon size="xs" variant="subtle">
                        <IconPencil />
                      </ThemeIcon>
                    ),
                    children: t("Edit"),
                    onClick: (e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      open()
                    },
                  },
                  {
                    leftSection: (
                      <ThemeIcon size="xs" c="red" variant="subtle">
                        <IconTrash />
                      </ThemeIcon>
                    ),
                    children: t("Delete"),
                    onClick: (e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      openConfirmModal({
                        title: t("DeleteEntity", { entity: "Post" }),
                        onConfirm: async () =>
                          await deletePost({
                            params: {
                              postId: feedPost.chatPostId,
                            },
                          }),
                        labels: {
                          cancel: t("Cancel"),
                          confirm: t("Delete"),
                        },
                      })
                    },
                  },
                ]}
              />
            ) : null
          }
          isDraft={feedPost.isDraft}
        />

        <Card.Section px="xs" mih={0} style={{ flexShrink: 0 }}>
          <Text truncate size="lg" fw="bold">
            {feedPost.title}
          </Text>
        </Card.Section>
        {feedPost.attachments.length > 0 && (
          <>
            <PostAttachments
              attachments={feedPost.attachments}
              chatPostId={feedPost.chatPostId}
            />
          </>
        )}
        <div className="feed-post-text-wrapper">
          <div
            ref={postTextRef}
            className={[
              "feed-post-text-content",
              isCollapsed ? "feed-post-text-content-collapsed" : undefined,
            ]
              .filter(Boolean)
              .join(" ")}
            dangerouslySetInnerHTML={{
              __html: sanitizeHTML(addStyleToLinks(feedPost.postText)),
            }}
          />
          {isCollapsed && isPostTextTruncated && (
            <Text size="sm" c="dimmed" fw={500} className="feed-post-read-more">
              {t("ReadMore")}
            </Text>
          )}
        </div>
        <PostReactionsAndComments
          feedPost={feedPost}
          hideComments={hideComments}
        />
      </Stack>
      <FeedPostFormModal
        key={feedPost.chatPostId}
        mode="edit"
        chatId={feedPost.chatId}
        chatPostDto={feedPost}
        opened={opened}
        onClose={close}
      />
    </Card>
  )
}
